'use client'

import { useMemo, type MouseEvent } from 'react'
import { useRouter } from 'next/navigation'
import { useNavigationTransition } from './NavigationFeedback'

/** Keep native behavior for downloads, new tabs, external URLs and anchors. */
export function shouldTrackLink(event: MouseEvent<HTMLAnchorElement>): boolean {
  const anchor = event.currentTarget
  if (
    event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey ||
    event.shiftKey || event.altKey || anchor.hasAttribute('download') ||
    (anchor.target && anchor.target !== '_self') || anchor.rel.split(/\s+/).includes('external')
  ) return false

  const destination = new URL(anchor.href, window.location.href)
  return destination.origin === window.location.origin &&
    (destination.pathname !== window.location.pathname || destination.search !== window.location.search)
}

export function useProgressLink({ onClick, replace, scroll }: {
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void
  replace?: boolean
  scroll?: boolean
}) {
  const router = useRouter()
  const startNavigation = useNavigationTransition()

  return (event: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event)
    if (!shouldTrackLink(event)) return
    // Read the rendered URL: next-intl has already resolved prefixes and synced
    // the language cookie. Prefetch, refs and normal anchor semantics stay intact.
    const href = event.currentTarget.href
    event.preventDefault()
    startNavigation(() => router[replace ? 'replace' : 'push'](href, { scroll }))
  }
}

/** Works for both Next's router and next-intl's locale-aware router. */
export function useProgressRouter<T extends ReturnType<typeof useRouter>>(router: T): T {
  const startNavigation = useNavigationTransition()
  return useMemo(() => ({
    ...router,
    push: (...args: Parameters<T['push']>) => startNavigation(() => router.push(args[0], args[1])),
    replace: (...args: Parameters<T['replace']>) => startNavigation(() => router.replace(args[0], args[1])),
    refresh: () => startNavigation(() => router.refresh()),
    back: () => startNavigation(() => router.back()),
    forward: () => startNavigation(() => router.forward()),
  }), [router, startNavigation])
}
